import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { getSupabaseClient } from '../../lib/supabase';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ConfirmModal } from './ConfirmModal';
import { Check, X, AlertTriangle, Globe } from 'lucide-react';

interface WhiteLabelConfig {
  merchant_id: string;
  business_name: string;
  custom_domain: string;
  brand_name: string;
  is_whitelabel_active: boolean;
  domain_verification_status: 'none' | 'pending' | 'verified' | 'rejected' | string;
  has_white_label_plan: boolean;
  created_at: string;
}

function VerificationBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    none: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    verified: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-semibold capitalize ${map[status] || map.none}`}>
      {status}
    </span>
  );
}

export function WhiteLabelControlView() {
  const [configs, setConfigs] = useState<WhiteLabelConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<WhiteLabelConfig | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [rejectReasons, setRejectReasons] = useState<Record<string, string>>({});

  const fetchConfigs = async () => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_list_whitelabel_configs');
    if (error) {
      toast.error(error.message || 'Failed to load white-label configs');
    } else {
      setConfigs(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleDecision = async (merchantId: string, decision: 'approved' | 'rejected') => {
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setProcessingId(merchantId);
    const { error } = await supabase.rpc('resolve_approval', {
      p_entity_type: 'white_label',
      p_entity_id: merchantId,
      p_decision: decision,
      p_reason: decision === 'rejected' ? (rejectReasons[merchantId] || null) : null,
    });
    if (error) {
      toast.error(error.message || 'Action failed');
    } else {
      toast.success(decision === 'approved' ? 'Domain approved' : 'Domain rejected');
      fetchConfigs();
    }
    setProcessingId(null);
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    const supabase = getSupabaseClient();
    if (!supabase) return;
    setProcessingId(revokeTarget.merchant_id);
    const { error } = await supabase.rpc('admin_revoke_whitelabel', {
      p_merchant_id: revokeTarget.merchant_id,
      p_reason: revokeReason.trim() || null,
    });
    if (error) {
      toast.error(error.message || 'Revoke failed');
    } else {
      toast.success('White-label revoked');
      setRevokeTarget(null);
      setRevokeReason('');
      fetchConfigs();
    }
    setProcessingId(null);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h1 className="text-2xl font-bold dark:text-white">White-Label Control</h1>
        <p className="text-slate-500 dark:text-slate-400">Domain verification, activation status and plan gating for all white-label tenants.</p>
      </div>

      <Card className="dark:bg-slate-900 dark:border-slate-800 border-none shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 font-medium">
              <tr>
                <th className="px-6 py-3">Business</th>
                <th className="px-6 py-3">Domain</th>
                <th className="px-6 py-3">Brand</th>
                <th className="px-6 py-3">Active?</th>
                <th className="px-6 py-3">Verification</th>
                <th className="px-6 py-3">Plan</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading && (
                <tr><td colSpan={7} className="px-6 py-8 text-center text-slate-500 dark:text-slate-400">Loading configs...</td></tr>
              )}
              {!loading && configs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    <Globe className="mx-auto mb-3 text-slate-300 dark:text-slate-700" size={32} />
                    No white-label configs found.
                  </td>
                </tr>
              )}
              {!loading && configs.map((c) => (
                <tr key={c.merchant_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-6 py-3 font-semibold dark:text-white">{c.business_name}</td>
                  <td className="px-6 py-3 font-mono text-xs dark:text-slate-300">{c.custom_domain}</td>
                  <td className="px-6 py-3 dark:text-slate-300">{c.brand_name}</td>
                  <td className="px-6 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${c.is_whitelabel_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                      {c.is_whitelabel_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-3"><VerificationBadge status={c.domain_verification_status} /></td>
                  <td className="px-6 py-3">
                    {c.has_white_label_plan ? (
                      <span className="text-emerald-600 dark:text-emerald-400 text-xs font-medium">Eligible</span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 text-xs font-medium" title="This tenant's current plan doesn't include white-label — activation will fail until upgraded">
                        <AlertTriangle size={14} /> No Plan
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <div className="flex flex-col gap-2 items-end">
                      {c.domain_verification_status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800/50"
                            disabled={processingId === c.merchant_id}
                            onClick={() => handleDecision(c.merchant_id, 'approved')}
                          >
                            <Check size={14} className="mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="bg-red-50 text-red-600 border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800/50"
                            disabled={processingId === c.merchant_id}
                            onClick={() => handleDecision(c.merchant_id, 'rejected')}
                          >
                            <X size={14} className="mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                      {c.domain_verification_status === 'pending' && (
                        <Input
                          placeholder="Rejection reason (optional)"
                          className="h-7 text-xs w-48"
                          value={rejectReasons[c.merchant_id] || ''}
                          onChange={(e) => setRejectReasons((prev) => ({ ...prev, [c.merchant_id]: e.target.value }))}
                        />
                      )}
                      {c.is_whitelabel_active && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 dark:border-red-800/50"
                          onClick={() => setRevokeTarget(c)}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmModal
        open={!!revokeTarget}
        title={`Revoke white-label for ${revokeTarget?.business_name}?`}
        description="Yeh unka custom domain/branding turant band kar dega."
        destructive
        confirmLabel="Revoke"
        loading={!!processingId}
        onConfirm={handleRevoke}
        onCancel={() => { setRevokeTarget(null); setRevokeReason(''); }}
      >
        <Input
          placeholder="Revoke reason (e.g. policy violation)"
          value={revokeReason}
          onChange={(e) => setRevokeReason(e.target.value)}
          className="dark:bg-slate-800 dark:border-slate-700 dark:text-white"
        />
      </ConfirmModal>
    </div>
  );
}

export default WhiteLabelControlView;
